import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Search, ShoppingBag, Heart, Menu, X } from "lucide-react";
import { useCart } from "@/context/CartContext";

const NAV = [
  { to: "/shop", label: "Shop" },
  { to: "/shop/earrings", label: "Earrings" },
  { to: "/shop/pendants", label: "Pendants" },
  { to: "/about", label: "Our Story" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const { totalItems, openCart } = useCart();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 backdrop-blur-xl bg-[#FAF8F5]/85 border-b border-[#DCD5CA]"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="flex items-center justify-between h-20">
          {/* Left: mobile menu + desktop nav */}
          <div className="flex-1 flex items-center">
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setMobileOpen(true)}
              className="md:hidden text-[#2A2626] p-2 -ml-2"
              aria-label="Open menu"
            >
              <Menu strokeWidth={1.5} size={22} />
            </button>
            <nav className="hidden md:flex items-center gap-10">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}-link`}
                  className={({ isActive }) =>
                    `text-[11px] uppercase tracking-[0.24em] font-sans text-[#2A2626] link-underline pb-1 ${
                      isActive ? "opacity-100" : "opacity-80 hover:opacity-100"
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
            </nav>
          </div>

          {/* Center: Logo */}
          <Link
            to="/"
            data-testid="site-logo-link"
            className="flex items-center gap-3 select-none"
          >
            <img
              src="https://res.cloudinary.com/drcoefnzk/image/upload/e_trim:10/f_auto,q_auto/v1783795248/Innayaa_Box_Logo_3.5x3.5_elmi29.png"
              alt="Innayaa"
              className="h-11 md:h-14 w-auto object-contain"
            />
          </Link>

          {/* Right: icons */}
          <div className="flex-1 flex items-center justify-end gap-5 text-[#2A2626]">
            <button
              data-testid="search-icon-button"
              aria-label="Search"
              className="hidden md:inline-flex hover:opacity-70 transition-opacity"
            >
              <Search strokeWidth={1.5} size={20} />
            </button>
            <button
              data-testid="wishlist-icon-button"
              aria-label="Wishlist"
              className="hidden md:inline-flex hover:opacity-70 transition-opacity"
            >
              <Heart strokeWidth={1.5} size={20} />
            </button>
            <button
              data-testid="cart-icon-button"
              aria-label="Cart"
              onClick={openCart}
              className="relative hover:opacity-70 transition-opacity"
            >
              <ShoppingBag strokeWidth={1.5} size={20} />
              {totalItems > 0 && (
                <span
                  data-testid="cart-count-badge"
                  className="absolute -top-2 -right-2 min-w-[18px] h-[18px] px-1 rounded-full bg-[#2A2626] text-[#FAF8F5] text-[10px] flex items-center justify-center font-sans"
                >
                  {totalItems}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div
          data-testid="mobile-menu-drawer"
          className="md:hidden fixed inset-0 z-50 bg-[#FAF8F5]"
        >
          <div className="flex items-center justify-between px-6 h-20 border-b border-[#DCD5CA]">
            <span className="font-serif text-xl">Menu</span>
            <button
              data-testid="mobile-menu-close"
              onClick={() => setMobileOpen(false)}
              className="p-2 -mr-2"
              aria-label="Close menu"
            >
              <X strokeWidth={1.5} size={22} />
            </button>
          </div>
          <nav className="flex flex-col px-8 py-10 gap-6">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                onClick={() => setMobileOpen(false)}
                data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="font-serif text-3xl text-[#2A2626]"
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}
